import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { useState, useMemo, useEffect } from "react";
import { Search, Plus, Pencil, Trash2, FolderTree } from "lucide-react";
import DashboardLayout from "../Layouts/DashboardLayout";
import ProtectedRoute from "../Components/ProtectedRoute";

export default function ChartOfAccounts() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    assets: 0,
    liabilities: 0,
    income: 0,
  });

  const [form, setForm] = useState({
    code: "",
    name: "",
    type: "Asset",
    group: "",
    opening_balance: "",
    status: "Active",
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      console.log("Loading accounts...");
      const [accountsRes, summaryRes] = await Promise.all([
        fetch("/api/accounts", {
          credentials: "include",
        }),
        fetch("/api/accounts/summary", {
          credentials: "include",
        }),
      ]);

      const accountsData = await accountsRes.json();
      const summaryData = await summaryRes.json();

      if (accountsData.status === "success") {
        setAccounts(accountsData.data);
      }

      if (summaryData.status === "success") {
        setStats(summaryData.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const filtered = useMemo(() => {
    return accounts.filter((a) => {
      const matchesSearch =
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.code.includes(search);

      const matchesType = type === "All" || a.type === type;

      return matchesSearch && matchesType;
    });
  }, [accounts, search, type]);

  return (
    <ProtectedRoute permission="accounting.coa">
      <DashboardLayout>
        <div className="space-y-10">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif">Chart of Accounts</h1>

              <p className="text-sm text-gray-400 mt-1">
                Manage financial accounts used across the ERP.
              </p>
            </div>

            <PermissionButton permissionModule="accounts" permissionAction="create"
              onClick={() => {
                setEditing(null);

                setForm({
                  code: "",
                  name: "",
                  type: "Asset",
                  group: "",
                  opening_balance: "",
                  status: "Active",
                });

                setShowModal(true);
              }}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-amber-400 text-black font-medium"
            >
              <Plus size={18} />
              New Account
            </PermissionButton>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Kpi title="Accounts" value={stats.total} />
            <Kpi title="Assets" value={stats.assets} />
            <Kpi title="Liabilities" value={stats.liabilities} />
            <Kpi title="Income" value={stats.income} />
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <div className="flex flex-wrap gap-4 justify-between">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-3 text-gray-500"
                />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search account..."
                  className="pl-10 pr-4 py-2 rounded-xl bg-black/40 border border-white/10"
                />
              </div>

              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="px-4 py-2 rounded-xl bg-black/40 border border-white/10"
              >
                <option>All</option>
                <option>Asset</option>
                <option>Liability</option>
                <option>Income</option>
                <option>Expense</option>
                <option>Equity</option>
              </select>
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
            <PaginatedTable className="w-full text-sm">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  <th className="px-6 py-4 text-left">Code</th>
                  <th className="px-6 py-4 text-left">Account</th>
                  <th className="px-6 py-4 text-left">Group</th>
                  <th className="px-6 py-4 text-left">Type</th>
                  <th className="px-6 py-4 text-right">Balance</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((a) => (
                  <tr
                    key={a.id}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="px-6 py-4">{a.code}</td>

                    <td className="px-6 py-4 font-medium flex items-center gap-2">
                      <FolderTree size={16} />

                      {a.name}
                    </td>

                    <td className="px-6 py-4">{a.group}</td>

                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-400 text-xs">
                        {a.type}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right">
                      ₹{Number(a.balance).toLocaleString("en-IN")}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs">
                        {a.status}
                      </span>
                    </td>

                    <td className="px-6 py-4"><TableActions>
                      <div className="flex justify-center gap-3">
                        <PermissionButton permissionModule="accounts" permissionAction="edit"
                          className="hover:text-amber-400"
                          onClick={() => {
                            setEditing(a);

                            setForm({
                              code: a.code,
                              name: a.name,
                              type: a.type,
                              group: a.group,
                              opening_balance: a.balance,
                              status: a.status,
                            });

                            setShowModal(true);
                          }}
                        >
                          <Pencil size={16} />
                        </PermissionButton>

                        <PermissionButton permissionModule="accounts"
                          className="hover:text-red-400"
                          onClick={async () => {
                            if (!window.confirm("Delete this account?")) return;

                            const res = await fetch(
                              `/api/accounts/${a.id}`,
                              {
                                method: "DELETE",
                                credentials: "include",
                              },
                            );

                            const data = await res.json();

                            if (data.status === "success") {
                              loadAccounts();
                            } else {
                              alert(data.message);
                            }
                          }}
                        >
                          <Trash2 size={16} />
                        </PermissionButton>
                      </div>
                    </TableActions></td>
                  </tr>
                ))}
              </tbody>
            </PaginatedTable>
          </div>
        </div>
        {showModal && (
          <AccountModal
            editing={editing}
            form={form}
            setForm={setForm}
            onClose={() => setShowModal(false)}
            onSave={async () => {
              if (!form.code.trim()) return alert("Account code required");
              if (!form.name.trim()) return alert("Account name required");
              if (!form.group.trim()) return alert("Group required");

              const url = editing
                ? `/api/accounts/${editing.id}`
                : "/api/accounts";

              const method = editing ? "PUT" : "POST";

              const res = await fetch(url, {
                method,
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(form),
              });

              const data = await res.json();

              if (data.status === "success") {
                loadAccounts();
                setShowModal(false);
              } else {
                alert(data.message);
              }
            }}
          />
        )}
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function Kpi({ title, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>

      <h2 className="mt-3 text-3xl font-serif">{value}</h2>
    </div>
  );
}

function AccountModal({ editing, form, setForm, onClose, onSave }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="w-full max-w-2xl rounded-2xl bg-[#09090B] border border-white/10 shadow-2xl">
        <div className="flex items-center justify-between px-8 py-5 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-serif">
              {editing ? "Edit Account" : "New Account"}
            </h2>

            <p className="text-xs text-gray-400 mt-1">
              Create or update ledger account
            </p>
          </div>

          <PermissionButton permissionModule="accounts"
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl"
          >
            ✕
          </PermissionButton>
        </div>

        <div className="p-8 grid grid-cols-2 gap-5">
          <div>
            <label className="text-xs text-gray-400">Account Code</label>

            <input
              disabled={editing}
              className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3 disabled:opacity-50"
              value={form.code}
              onChange={(e) =>
                setForm({
                  ...form,
                  code: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="text-xs text-gray-400">Account Name</label>

            <input
              className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              value={form.name}
              onChange={(e) =>
                setForm({
                  ...form,
                  name: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="text-xs text-gray-400">Account Type</label>

            <select
              className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              value={form.type}
              onChange={(e) =>
                setForm({
                  ...form,
                  type: e.target.value,
                })
              }
            >
              <option>Asset</option>
              <option>Liability</option>
              <option>Income</option>
              <option>Expense</option>
              <option>Equity</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-400">Group</label>

            <input
              className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              value={form.group}
              onChange={(e) =>
                setForm({
                  ...form,
                  group: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="text-xs text-gray-400">Opening Balance</label>

            <input
              type="number"
              className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              value={form.opening_balance}
              onChange={(e) =>
                setForm({
                  ...form,
                  opening_balance: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="text-xs text-gray-400">Status</label>

            <select
              className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              value={form.status}
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value,
                })
              }
            >
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-8 py-5 border-t border-white/10">
          <PermissionButton permissionModule="accounts"
            onClick={onClose}
            className="px-6 py-3 rounded-xl border border-white/10 hover:bg-white/5"
          >
            Cancel
          </PermissionButton>

          <PermissionButton permissionModule="accounts"
            disabled={!form.code || !form.name || !form.group}
            onClick={onSave}
            className={`px-8 py-3 rounded-xl font-semibold ${
              !form.code || !form.name || !form.group
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-amber-400 text-black"
            }`}
          >
            {editing ? "Update Account" : "Create Account"}
          </PermissionButton>
        </div>
      </div>
    </div>
  );
}
